import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiClient } from '../services/api';

/**
 * 认证状态管理
 * 使用 Zustand 进行状态管理，支持持久化
 */
export const useAuthStore = create(
	persist(
		(set, get) => ({
			// 状态
			user: null,
			token: null,
			isAuthenticated: false,
			isLoading: false,
			
			// 登录
			login: async (credentials) => {
				set({ isLoading: true });
				
				try {
					const response = await apiClient.post('/auth/login', credentials);
					const { user, token } = response.data;
					
					// 设置 API 客户端的默认 Authorization 头
					apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
					
					set({
						user,
						token,
						isAuthenticated: true,
						isLoading: false
					});
					
					return { success: true, user, token };
					
				} catch (error) {
					set({ isLoading: false });
					
					const message = error.response?.data?.error?.message || 'Login failed';
					return { success: false, error: message };
				}
			},
			
			// 注册
			register: async (userData) => {
				set({ isLoading: true });
				
				try {
					const response = await apiClient.post('/auth/register', userData);
					const { user, token } = response.data;
					
					// 设置 API 客户端的默认 Authorization 头
					apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
					
					set({
						user,
						token,
						isAuthenticated: true,
						isLoading: false
					});
					
					return { success: true, user, token };
					
				} catch (error) {
					set({ isLoading: false });
					
					const message = error.response?.data?.error?.message || 'Registration failed';
					return { success: false, error: message };
				}
			},
			
			// 登出
			logout: async () => {
				try {
					// 调用后端登出接口（记录日志）
					if (get().isAuthenticated) {
						await apiClient.post('/auth/logout');
					}
				} catch (error) {
					console.error('Logout API call failed:', error);
				} finally {
					// 清除本地状态
					delete apiClient.defaults.headers.common['Authorization'];
					
					set({
						user: null,
						token: null,
						isAuthenticated: false,
						isLoading: false
					});
				}
			},
			
			// 刷新用户信息
			refreshUser: async () => {
				if (!get().isAuthenticated) return;
				
				try {
					const response = await apiClient.get('/auth/me');
					const { user } = response.data;
					
					set({ user });
					return { success: true, user };
					
				} catch (error) {
					console.error('Failed to refresh user:', error);
					
					// 如果是认证错误，自动登出
					if (error.response?.status === 401) {
						get().logout();
					}
					
					return { success: false, error: error.message };
				}
			},
			
			// 刷新令牌
			refreshToken: async () => {
				if (!get().isAuthenticated) return;
				
				try {
					const response = await apiClient.post('/auth/refresh');
					const { token } = response.data;
					
					// 更新 API 客户端的 Authorization 头
					apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
					
					set({ token });
					return { success: true, token };
					
				} catch (error) {
					console.error('Failed to refresh token:', error);
					
					// 如果刷新失败，自动登出
					get().logout();
					return { success: false, error: error.message };
				}
			},
			
			// 修改密码
			changePassword: async (passwordData) => {
				set({ isLoading: true });
				
				try {
					await apiClient.post('/auth/change-password', passwordData);
					
					set({ isLoading: false });
					return { success: true };
					
				} catch (error) {
					set({ isLoading: false });
					
					const message = error.response?.data?.error?.message || 'Password change failed';
					return { success: false, error: message };
				}
			},
			
			// 初始化认证状态
			initialize: () => {
				const { token, user } = get();
				
				if (token && user) {
					// 设置 API 客户端的默认 Authorization 头
					apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
					
					// 验证令牌是否仍然有效
					get().refreshUser().then(result => {
						if (!result.success) {
							console.log('Token expired, logging out');
							get().logout();
						}
					});
				}
			}
		}),
		{
			name: 'auth-storage',
			partialize: (state) => ({
				user: state.user,
				token: state.token,
				isAuthenticated: state.isAuthenticated
			})
		}
	)
);

// 初始化认证状态
useAuthStore.getState().initialize();
